import { DateTime } from "luxon";
import { MB } from "@shared/constants/constants.ts";
import { ConsumptionCsvGroupBy } from "@shared/services/usage-explorer/fetchConsumptionCsv.ts";
import { CONSUMPTION_GROUP_BY_DIMENSIONS_BY_STORAGE_TYPE } from "@shared/components/usage-explorer/storage-type-picker/helper.ts";
import {
  TourConsumptionMonth,
  TourConsumptionRequest,
} from "@shared/types/localTypes/tour.ts";
import tourConsumptionMembers from "@shared/services/tour/mockedApis/usageExplorer/tour-consumption-members.ts";
import sortTourConsumptionRows from "@shared/services/tour/mockedApis/usageExplorer/sort-tour-consumption-rows.ts";

type BuildTourConsumptionMonths = (args: {
  readonly request: TourConsumptionRequest;
}) => ReadonlyArray<TourConsumptionMonth>;

const MAX_TOUR_CONSUMPTION_MONTHS = 24;

const STORAGE_MONTHLY_GROWTH = 1.03;

const TRAFFIC_WOBBLE = [
  1024, 968, 1071, 1003, 942, 1050, 990, 1016, 951, 1037, 979, 1008,
] as const;

const buildTourConsumptionMonths: BuildTourConsumptionMonths = ({
  request,
}) => {
  const firstMonth = DateTime.fromISO(request.start_date, {
    zone: "utc",
  }).startOf("month");
  const lastMonth = DateTime.fromISO(request.end_date, {
    zone: "utc",
  }).startOf("month");
  const currentMonth = DateTime.utc().startOf("month");
  const monthCount = Math.min(
    MAX_TOUR_CONSUMPTION_MONTHS,
    Math.max(0, Math.round(lastMonth.diff(firstMonth, "months").months) + 1),
  );
  const shownFirstMonth = lastMonth.minus({ months: monthCount - 1 });
  const groupBys: ReadonlyArray<ConsumptionCsvGroupBy> =
    CONSUMPTION_GROUP_BY_DIMENSIONS_BY_STORAGE_TYPE[request.storage_type];

  return Array.from({ length: monthCount }, (_, index) => {
    const date = shownFirstMonth.plus({ months: index });
    const monthsAgo = Math.max(
      0,
      Math.round(currentMonth.diff(date, "months").months),
    );
    const storageUnits = Math.ceil(1024 / STORAGE_MONTHLY_GROWTH ** monthsAgo);
    const trafficUnits = Math.max(
      400,
      TRAFFIC_WOBBLE[monthsAgo % TRAFFIC_WOBBLE.length] - 12 * monthsAgo,
    );

    return {
      date,
      rows: Object.fromEntries(
        groupBys.map((groupBy) => [
          groupBy,
          sortTourConsumptionRows(
            tourConsumptionMembers({
              groupBy,
              storageType: request.storage_type,
            }).map((member) => ({
              jpd: member.jpd,
              name: member.name,
              trafficBytes: member.trafficGb * trafficUnits * MB,
              storageBytes: member.storageGb * storageUnits * MB,
            })),
          ),
        ]),
      ),
    };
  });
};

export default buildTourConsumptionMonths;
